import { cn } from "@/lib/utils";

import { CopyButton } from "@/components/ui/copy-button";

import { HighlightedCode, languageLabel } from "@/features/docs/lib/highlight";

/**
 * Code sample with a title bar, language pill, and copy button.
 *
 * The surface is dark in BOTH themes on purpose — it's the convention every
 * modern doc site follows (Stripe, Railway, Tailwind), and it makes samples
 * read as terminal output rather than as page content.
 *
 * Chrome and syntax highlighting both come from the docs template redesign
 * (variant C / Runpod, 2026-08-03): a floating rounded-2xl panel with a primary
 * wash across the title bar, matching the panels around it. The samples used to
 * render unhighlighted — the legacy tokenizer now lives in `lib/highlight`.
 */
export function CodeBlock({
  code,
  language = "typescript",
  title,
  className,
}: {
  code: string;
  language?: string;
  title?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-hairline bg-[hsl(var(--docs-nav-rest))] shadow-[0_16px_40px_-28px_rgb(0_0_0/0.9)]",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3 border-b border-hairline bg-elevated py-2 pl-4 pr-2">
        {/* An untitled block leaves this side BLANK, as the legacy component
         * does. It used to fall back to the language name, which put e.g.
         * "TYPESCRIPT" on the left of every untitled sample — a duplicate of
         * the language pill on the right, and a label the old docs never had.
         * The span stays rendered so `justify-between` keeps the pill and the
         * copy button hard right. */}
        <span className="truncate font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-primary-text">
          {title}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-full bg-elevated px-2 py-0.5 text-[0.625rem] font-medium text-muted-foreground">
            {languageLabel(language)}
          </span>
          <CopyButton
            value={code}
            label="Copy"
            variant="ghost"
            size="sm"
            className="h-7 rounded-full px-2.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
          />
        </div>
      </div>
      <pre className="overflow-x-auto p-4 text-[0.8125rem] leading-relaxed">
        <HighlightedCode code={code} language={language} />
      </pre>
    </div>
  );
}
